import { useEffect, useMemo } from 'react';
import { useQuery } from '@apollo/client/react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { type SxProps } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

import {
    AiUsageDocument,
    type IAiUsageQuery,
    type IAiUsageQueryVariables,
} from 'graphql/types';

interface IProps {
    sx?: SxProps;
    onLimitReached?(reached: boolean): void;
}

export const AIUsageIndicator = ({ sx, onLimitReached }: IProps) => {
    const [translate] = useTranslation();
    const { data, loading, error } = useQuery<
        IAiUsageQuery,
        IAiUsageQueryVariables
    >(AiUsageDocument, { fetchPolicy: 'cache-and-network' });

    // aiAllowance of 0 means unlimited — don't render the indicator
    const { aiAllowance = 0, summary } = data?.organisation ?? {};
    const used = summary?.aiUsed ?? 0;
    const limitReached = aiAllowance > 0 && used >= aiAllowance;
    const percentage =
        aiAllowance > 0 ? Math.min((used / aiAllowance) * 100, 100) : 0;

    const color: 'primary' | 'warning' | 'error' = limitReached
        ? 'error'
        : percentage >= 90
          ? 'warning'
          : 'primary';

    // Usage resets on the first of next month
    const resetDate = useMemo(
        () =>
            DateTime.now()
                .plus({ months: 1 })
                .startOf('month')
                .toFormat('d MMMM yyyy'),
        []
    );

    useEffect(() => {
        if (!loading) {
            onLimitReached?.(limitReached);
        }
    }, [limitReached, onLimitReached, loading]);

    // On error, unblock the form so the user isn't stuck
    useEffect(() => {
        if (error) onLimitReached?.(false);
    }, [error, onLimitReached]);

    if (!loading && (error || !data?.organisation || !aiAllowance)) return null;

    return (
        <Box sx={sx}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                    visibility: loading ? 'hidden' : 'visible',
                }}
            >
                <Typography
                    color={
                        color === 'primary' ? 'text.secondary' : `${color}.dark`
                    }
                    sx={{ fontSize: '1.4rem', lineHeight: 1.2 }}
                >
                    {limitReached
                        ? translate('aiUsage.limitReached')
                        : translate('aiUsage.percentUsed', {
                              percent: Math.round(percentage),
                          })}
                </Typography>
                <Typography
                    color="text.secondary"
                    sx={{
                        fontSize: '1.4rem',
                        lineHeight: 1.2,
                        textAlign: 'right',
                    }}
                >
                    {translate('aiUsage.resetsOn', { date: resetDate })}
                </Typography>
            </Box>
            {loading ? (
                <LinearProgress
                    sx={{
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': { bgcolor: 'grey.400' },
                    }}
                />
            ) : (
                <LinearProgress
                    color={color}
                    sx={{
                        borderRadius: 1,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': { transition: 'none' },
                    }}
                    value={percentage}
                    variant="determinate"
                />
            )}
        </Box>
    );
};
